import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import { useAuth } from '../../auth/AuthContext';

const managerEmail = 'store0000@uppals.com';
const apiRoot = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function formatPhoneNumber(value) {
  const digits = (value || '').replace(/\D/g, '');
  return digits.length === 10
    ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : value;
}

function formatHolidayDate(holiday) {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  const from = new Date(holiday.fromDate).toLocaleDateString('en-US', options);
  const to = new Date(holiday.toDate).toLocaleDateString('en-US', options);
  return from === to ? from : `${from} – ${to}`;
}

export default function HolidaySubscriptionManager() {
  const { user, getAccessToken } = useAuth();
  const [data, setData] = useState({ holidays: [], companies: [] });
  const [holidayId, setHolidayId] = useState('');
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const request = useCallback(async (path, options = {}) => {
    const token = await getAccessToken();
    const response = await fetch(`${apiRoot}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(body.replace(/^"|"$/g, '') || 'The request could not be completed.');
    }
    return response.json();
  }, [getAccessToken]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request('/api/Holiday/GetHolidaySubscriptionManagement')
      .then((result) => {
        if (cancelled) return;
        setData(result);
        if (result.holidays.length) setHolidayId(result.holidays[0].holidayId);
      })
      .catch((requestError) => !cancelled && setError(requestError.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [request]);

  const visibleCompanies = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data.companies;
    return data.companies
      .map((company) => ({
        ...company,
        phoneNumbers: company.companyName.toLowerCase().includes(term)
          ? company.phoneNumbers
          : company.phoneNumbers.filter((phone) => phone.phoneNumberDigits.includes(term.replace(/\D/g, '')))
      }))
      .filter((company) => company.phoneNumbers.length > 0);
  }, [data.companies, search]);

  const selectedSet = useMemo(() => new Set(selectedPhoneNumbers), [selectedPhoneNumbers]);
  const selectedHoliday = data.holidays.find((holiday) => holiday.holidayId === holidayId);
  const visiblePhoneNumbers = useMemo(
    () => visibleCompanies.flatMap((company) => company.phoneNumbers),
    [visibleCompanies]
  );
  const sortedRows = useMemo(() => {
    const rows = visibleCompanies.flatMap((company) => company.phoneNumbers.map((phone) => ({
      company,
      phone,
      subscribed: phone.subscribedHolidayIds.includes(holidayId)
    })));
    const direction = sortDirection === 'asc' ? 1 : -1;
    return rows.sort((left, right) => {
      if (sortBy === 'subscription') {
        const statusDifference = Number(left.subscribed) - Number(right.subscribed);
        if (statusDifference !== 0) return statusDifference * direction;
      }
      return left.company.companyName.localeCompare(right.company.companyName) * direction;
    });
  }, [holidayId, sortBy, sortDirection, visibleCompanies]);

  const changeSort = (column) => {
    if (sortBy === column) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const togglePhoneNumber = (phoneNumberId) => {
    setSelectedPhoneNumbers((current) => current.includes(phoneNumberId)
      ? current.filter((id) => id !== phoneNumberId)
      : [...current, phoneNumberId]);
  };

  const selectBySubscription = (isSubscribed) => {
    setSelectedPhoneNumbers(visiblePhoneNumbers
      .filter((phone) => phone.subscribedHolidayIds.includes(holidayId) === isSubscribed)
      .map((phone) => phone.phoneNumberId));
  };

  const save = async (isSubscribed) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const result = await request('/api/Holiday/UpdateHolidaySubscriptions', {
        method: 'POST',
        body: JSON.stringify({ holidayId, phoneNumberIds: selectedPhoneNumbers, isSubscribed })
      });
      setData(result);
      setMessage(`${selectedPhoneNumbers.length} phone number${selectedPhoneNumbers.length === 1 ? '' : 's'} ${isSubscribed ? 'subscribed' : 'unsubscribed'}.`);
      setSelectedPhoneNumbers([]);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  if ((user.email || '').trim().toLowerCase() !== managerEmail) {
    return <Alert severity="error">You do not have access to holiday subscription management.</Alert>;
  }

  if (loading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}><CircularProgress /></Box>;
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <EventAvailableRoundedIcon color="primary" />
          <Typography variant="h5" fontWeight={800}>Holiday subscriptions</Typography>
        </Stack>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
          Subscribe or unsubscribe a global holiday for multiple companies and phone numbers at once.
        </Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" onClose={() => setMessage('')}>{message}</Alert>}

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: { md: 360 }, flex: 1 }}>
            <InputLabel id="holiday-manager-label">Holiday</InputLabel>
            <Select
              labelId="holiday-manager-label"
              label="Holiday"
              value={holidayId}
              onChange={(event) => {
                setHolidayId(event.target.value);
                setSelectedPhoneNumbers([]);
                setMessage('');
              }}
            >
              {data.holidays.map((holiday) => (
                <MenuItem key={holiday.holidayId} value={holiday.holidayId}>
                  {holiday.holidayName} — {formatHolidayDate(holiday)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Company name or phone number"
            size="small"
            sx={{ minWidth: { md: 300 }, flex: 1 }}
          />
          {selectedHoliday && <Chip label={formatHolidayDate(selectedHoliday)} size="small" />}
          {selectedHoliday && (
            <Chip
              label={selectedHoliday.isBusinessClosed ? 'Closed' : 'Greeting'}
              size="small"
              color={selectedHoliday.isBusinessClosed ? 'warning' : 'default'}
            />
          )}
        </Stack>
      </Paper>

      {!data.holidays.length && <Alert severity="info">There are no active global holidays to manage.</Alert>}

      {holidayId && <>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" onClick={() => setSelectedPhoneNumbers(visiblePhoneNumbers.map((phone) => phone.phoneNumberId))}>
            Select all
          </Button>
          <Button size="small" variant="outlined" onClick={() => setSelectedPhoneNumbers([])}>
            Unselect all
          </Button>
          <Button size="small" variant="text" onClick={() => selectBySubscription(true)}>
            Select subscribed
          </Button>
          <Button size="small" variant="text" onClick={() => selectBySubscription(false)}>
            Select unsubscribed
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: { sm: 'auto !important' } }}>
            {visiblePhoneNumbers.length} phone number{visiblePhoneNumbers.length === 1 ? '' : 's'} shown
          </Typography>
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" aria-label="Holiday subscription companies and phone numbers">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell sx={{ fontWeight: 800 }}>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortDirection : 'asc'}
                    onClick={() => changeSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Phone number</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, width: 165 }}>
                  <TableSortLabel
                    active={sortBy === 'subscription'}
                    direction={sortBy === 'subscription' ? sortDirection : 'asc'}
                    onClick={() => changeSort('subscription')}
                  >
                    Subscription
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map(({ company, phone, subscribed }) => {
                return (
                  <TableRow key={phone.phoneNumberId} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedSet.has(phone.phoneNumberId)}
                        onChange={() => togglePhoneNumber(phone.phoneNumberId)}
                        inputProps={{ 'aria-label': `Select ${company.companyName} ${phone.phoneNumberDigits}` }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.35, fontWeight: 750 }}>{company.companyName}</TableCell>
                    <TableCell sx={{ py: 0.35, fontWeight: 650 }}>{formatPhoneNumber(phone.phoneNumberDigits)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.35, width: 145 }}>
                      <Chip
                        label={subscribed ? 'Subscribed' : 'Not subscribed'}
                        color={subscribed ? 'success' : 'default'}
                        variant={subscribed ? 'filled' : 'outlined'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {!sortedRows.length && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>No matching companies or phone numbers.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>}

      <Paper
        elevation={6}
        sx={{ position: 'sticky', bottom: 8, p: 1.25, borderRadius: 2, zIndex: 2 }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1}>
          <Typography variant="body2" fontWeight={700}>
            {selectedPhoneNumbers.length} phone number{selectedPhoneNumbers.length === 1 ? '' : 's'} selected
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="error"
              disabled={!selectedPhoneNumbers.length || saving}
              onClick={() => save(false)}
            >
              Unsubscribe
            </Button>
            <Button
              variant="contained"
              disabled={!selectedPhoneNumbers.length || saving}
              onClick={() => save(true)}
            >
              {saving ? 'Saving…' : 'Subscribe'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
